/**
 * Task status visual identity — single source of truth for the colours,
 * icons, and labels that represent task statuses across the app (progress
 * bars, stat labels, subtask rows, …). Change the values here to re-theme
 * every consumer in one edit.
 *
 * NOTE: Tailwind's JIT only picks up *complete* class strings it can see at
 * build time, so consumers must reference these fields directly (e.g.
 * `className={TASK_STATUS_COLORS[TaskStatus.COMPLETED].bar}`) rather than
 * interpolating the colour name. Keep the values as literal utility classes.
 */
import { SuccessIcon, TimeIcon, StatusPendingIcon, StatusRejectedIcon, StatusNotApplicableIcon, StatusCannotMeasureIcon, AlertIcon, HelpIcon, type LucideIcon } from "@/lib/icons";
import { TaskStatus } from "@/types";

export const TASK_STATUS_COLORS = {
  /**
   * bar: Solid segment colour in a progress bar.
   * track: Pending has no dedicated segment — it's the *remaining* space in the bar. `track` is that empty track background.
   * text: Accompanying label text colour (e.g. "3 valmis").
   * iconButton: Text colour classes for icon buttons (base + hover), used in StatusCheckmark.
   */
  [TaskStatus.COMPLETED]: {
    bar: "bg-green-400",
    text: "text-green-600",
    iconButton: "text-green-400 hover:text-green-600",
  },
  [TaskStatus.IN_PROGRESS]: {
    bar: "bg-yellow-400",
    text: "text-yellow-600",
    iconButton: "text-yellow-400 hover:text-yellow-600",
  },
  [TaskStatus.PENDING]: {
    track: "bg-slate-200",
    text: "text-slate-400",
    iconButton: "text-slate-400 hover:text-slate-600",
  },
  [TaskStatus.REJECTED]: {
    bar: "bg-red-400",
    text: "text-red-600",
    iconButton: "text-red-400 hover:text-red-600",
  },
  [TaskStatus.CANNOT_MEASURE]: {
    bar: "bg-red-400",
    text: "text-red-600",
    iconButton: "text-red-400 hover:text-red-600",
  },
  [TaskStatus.NOT_APPLICABLE]: {
    bar: "bg-red-400",
    text: "text-red-600",
    iconButton: "text-slate-400 hover:text-slate-600",
  },
  [TaskStatus.PROBLEM]: {
    bar: "bg-red-400",
    text: "text-red-600",
    iconButton: "text-red-400 hover:text-red-600",
  },
  [TaskStatus.UNKNOWN]: {
    bar: "bg-blue-400",
    text: "text-blue-600",
    iconButton: "text-blue-400 hover:text-red-600",
  },
} as const;

/**
 * Matching icon set. Mirrors the symbols used in
 * `components/tasks/TaskDialog.tsx` so progress summaries visually align
 * with the per-task status controls users interact with elsewhere.
 */
export const TASK_STATUS_ICONS: Record<TaskStatus, LucideIcon> = {
  [TaskStatus.COMPLETED]: SuccessIcon,
  [TaskStatus.IN_PROGRESS]: TimeIcon,
  [TaskStatus.PENDING]: StatusPendingIcon,
  [TaskStatus.REJECTED]: StatusRejectedIcon,
  [TaskStatus.CANNOT_MEASURE]: StatusCannotMeasureIcon,
  [TaskStatus.NOT_APPLICABLE]: StatusNotApplicableIcon,
  [TaskStatus.PROBLEM]: AlertIcon,
  [TaskStatus.UNKNOWN]: HelpIcon,
};

export const TASK_STATUS_LABELS_SINGULAR: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "Aloittamatta",
  [TaskStatus.IN_PROGRESS]: "Kesken",
  [TaskStatus.COMPLETED]: "Valmis",
  [TaskStatus.REJECTED]: "Hylätty",
  [TaskStatus.CANNOT_MEASURE]: "Ei tarkastettavissa",
  [TaskStatus.NOT_APPLICABLE]: "Ei asiaankuuluva",
  [TaskStatus.PROBLEM]: "Odottaa aliurakoitsijaa",
  [TaskStatus.UNKNOWN]: "Tuntematon",
};

export const TASK_STATUS_LABELS_PLURAL: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "Aloittamatta",
  [TaskStatus.IN_PROGRESS]: "Kesken",
  [TaskStatus.COMPLETED]: "Valmiit",
  [TaskStatus.REJECTED]: "Hylätyt",
  [TaskStatus.CANNOT_MEASURE]: "Ei tarkastettavissa",
  [TaskStatus.NOT_APPLICABLE]: "Ei asiaankuuluvia",
  [TaskStatus.PROBLEM]: "Odottavat aliurakoitsijaa",
  [TaskStatus.UNKNOWN]: "Tuntemattomia",
};

/**
 * Faint row background tint applied per task status (e.g. table rows in
 * Tilannekatsaus). Kept light enough to stay readable under text and to let
 * row hover states still register. Literal class strings for Tailwind JIT.
 */
export const TASK_ROW_BG_BY_STATUS: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "bg-white",
  [TaskStatus.IN_PROGRESS]: "bg-yellow-50",
  [TaskStatus.COMPLETED]: "bg-green-50",
  [TaskStatus.REJECTED]: "bg-red-50",
  [TaskStatus.CANNOT_MEASURE]: "bg-red-50",
  [TaskStatus.NOT_APPLICABLE]: "bg-red-50",
  [TaskStatus.PROBLEM]: "bg-red-50",
  [TaskStatus.UNKNOWN]: "bg-blue-50",
};

/**
 * Hover background tint per task status — one shade darker than
 * TASK_ROW_BG_BY_STATUS so a coloured row deepens in its own hue on hover
 * instead of falling back to the table's default muted gray. The `!` ensures
 * it overrides `hover:bg-muted/50`. Literal class strings for Tailwind JIT.
 */
export const TASK_ROW_HOVER_BG_BY_STATUS: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "hover:!bg-slate-100",
  [TaskStatus.IN_PROGRESS]: "hover:!bg-yellow-100",
  [TaskStatus.COMPLETED]: "hover:!bg-green-100",
  [TaskStatus.REJECTED]: "hover:!bg-red-100",
  [TaskStatus.CANNOT_MEASURE]: "hover:!bg-red-100",
  [TaskStatus.NOT_APPLICABLE]: "hover:!bg-red-100",
  [TaskStatus.PROBLEM]: "hover:!bg-red-100",
  [TaskStatus.UNKNOWN]: "hover:!bg-blue-100",
};

/**
 * Left-border colour applied per task status.
 */
export const TASK_BORDER_BY_STATUS: Record<TaskStatus, string> = {
  // REJECTED, CANNOT_MEASURE, NOT_APPLICABLE, UNKNOWN Should not be used, but if it is,
  // it gets a neutral blue border to distinguish from actual problems.
  [TaskStatus.PENDING]: "border-l-slate-400",
  [TaskStatus.IN_PROGRESS]: "border-l-yellow-400",
  [TaskStatus.COMPLETED]: "border-l-green-400",
  [TaskStatus.REJECTED]: "border-l-blue-400",
  [TaskStatus.CANNOT_MEASURE]: "border-l-blue-400",
  [TaskStatus.NOT_APPLICABLE]: "border-l-blue-400",
  [TaskStatus.PROBLEM]: "border-l-red-400",
  [TaskStatus.UNKNOWN]: "border-l-blue-400",
};
